import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Grid, Container, Typography } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import GavelIcon from '@mui/icons-material/Gavel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNavigate } from 'react-router-dom';
import { toast } from '../components/toast';
import { getDashboardInfo } from '../services/api';
import { getUserGroups } from '../utils';
import { useLoadingState } from '../hooks/useLoadingState';
import {
  NavigationCard,
  DashboardSection,
  StatCard,
  QuickActionsSection,
  SpeedDialActions,
} from '../components/admin-dashboard';

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [adminDashboardInfo, setAdminDashboardInfo] = useState(null);
  const { isLoading, withLoading } = useLoadingState({
    minLoadingTime: 800,
  });

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !userGroups.includes('admin')) {
      navigate('/login', { replace: true });
      return;
    }

    withLoading(async () => {
      const response = await getDashboardInfo();
      setAdminDashboardInfo(response.data.info);
      return response.data.info;
    }, 'Loading dashboard statistics...').catch(() => {
      toast.error('Cannot get admin dashboard info, try again later.');
    });
  }, [navigate, withLoading]);

  // test

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Admin Dashboard</title>
      </Helmet>
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom>
              Admin Dashboard
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Hi, {localStorage.getItem('username')}!
            </Typography>
          </Grid>

          <QuickActionsSection onNavigate={navigate} />

          <Grid container spacing={2} px={2}>
            <DashboardSection title="Clients & Agreements" isLoading={isLoading} skeletonCount={4}>
              <Grid item xs={12} sm={6} md={3}>
                <NavigationCard
                  title="Total Clients"
                  icon={<PeopleAltIcon />}
                  value={adminDashboardInfo?.total_clients || 0}
                  onClick={() => navigate('/clients')}
                />
              </Grid>
              <StatCard
                title="Total Agreements"
                icon={<GavelIcon />}
                value={adminDashboardInfo?.total_contracts || 0}
                iconColor="primary"
              />
              <StatCard
                title="Signed Agreements"
                icon={<GavelIcon />}
                value={adminDashboardInfo?.total_signed_contracts || 0}
                iconColor="primary"
              />
              <StatCard
                title="Pending Agreements"
                icon={<HourglassEmptyIcon />}
                value={adminDashboardInfo?.total_pending_contracts || 0}
                iconColor="warning"
              />
            </DashboardSection>
            <DashboardSection
              title="Support Tickets"
              isLoading={isLoading}
              skeletonCount={3}
              gridProps={{ xs: 12, sm: 6, md: 4 }}
              sx={{ mt: 3 }}
            >
              <Grid item xs={12} sm={6} md={4}>
                <NavigationCard
                  title="Total Tickets"
                  icon={<SupportAgentIcon />}
                  value={adminDashboardInfo?.total_support_tickets || 0}
                  onClick={() => navigate('/support-tickets')}
                />
              </Grid>
              <StatCard
                title="Open Tickets"
                icon={<ErrorOutlineIcon />}
                value={adminDashboardInfo?.total_open_tickets || 0}
                iconColor="warning"
                mdSize={4}
              />
              <StatCard
                title="Closed Tickets"
                icon={<DoneAllIcon />}
                value={adminDashboardInfo?.total_closed_tickets || 0}
                iconColor="success"
                mdSize={4}
              />
            </DashboardSection>
          </Grid>
        </Grid>
      </Container>

      <SpeedDialActions onNavigate={navigate} />
    </>
  );
}
